import React, { useEffect, useRef, useState } from 'react';
import { BlockInfoType } from '../../common-types/BlockInfo.type';

const MAX_MILLISECONDS_BEFORE_LAUNCHING_GAME = 3000;

interface Props {
  dataTemplateType: string;
  dataTemplateConfig: string;
  displayName: string;
  addEditableBlock?: (block: BlockInfoType) => void;
  removeEditableBlock?: (configPath: string) => void;
  onBlockEdit?: React.MouseEventHandler<HTMLDivElement>;
  onLoad?: () => void;
  neededAssets: (string | null | undefined)[];
  isEdition: boolean;
  className?: string;
}

export const CommonPluginContainer: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  dataTemplateType,
  dataTemplateConfig,
  displayName,
  addEditableBlock,
  removeEditableBlock,
  onBlockEdit,
  onLoad,
  neededAssets,
  isEdition,
  className,
  children,
}) => {
  const [
    assetsCount,
    setAssetsCount,
  ] = useState(0);
  const [
    loadedAssets,
    setLoadedAssets,
  ] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>();
  const onLoadAlreadyCalledRef = useRef(false);

  useEffect(
    () => {
      if (addEditableBlock) {
        addEditableBlock({
          blockType: dataTemplateType,
          configPath: dataTemplateConfig,
          label: displayName,
        });
      }
      return () => {
        if (removeEditableBlock) {
          removeEditableBlock(dataTemplateConfig);
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (isEdition || onLoadAlreadyCalledRef.current) {
      return;
    }
    timeoutRef.current = setTimeout(() => {
      if (onLoad) {
        onLoad();
        onLoadAlreadyCalledRef.current = true;
      }
    }, MAX_MILLISECONDS_BEFORE_LAUNCHING_GAME);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (onLoad === undefined || isEdition || onLoadAlreadyCalledRef.current) {
      return;
    }
    const hasNoNeededAsset = neededAssets.every((value) => value === null || value === undefined);
    const neededAssetsHasBeenLoaded = assetsCount > 0 && loadedAssets === assetsCount;
    if (hasNoNeededAsset || neededAssetsHasBeenLoaded) {
      onLoad();
      clearTimeout(timeoutRef.current);
      onLoadAlreadyCalledRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedAssets, assetsCount]);

  return (
    <div
      onClick={onBlockEdit}
      data-template-type={dataTemplateType}
      data-display-name={displayName}
      data-config-path={dataTemplateConfig}
      className={className}
      ref={(ref): void => {
        if (isEdition) {
          return;
        }
        if (ref !== null) {
          const foundElements: NodeListOf<HTMLVideoElement | HTMLImageElement> = ref.querySelectorAll('img, video');
          foundElements.forEach((element, index): void => {
            foundElements[index].onload = (): void => {
              setLoadedAssets((prev) => prev + 1);
            };
          });
          setAssetsCount(foundElements.length);
        }
      }}
    >
      {children}
    </div>
  );
};
